import {
  createStore,
  patients,
  getPatient,
  patientMatches,
  resultsFor,
  medicationsFor,
  catalogs,
  AppError,
  STORAGE_KEY,
  type Encounter,
  type Order,
  type Result,
  type Note,
} from "./domain.js";
import { randomUuid } from "@talkie/agentic-app-transport";
import {
  loadWorkspace,
  saveWorkspace,
  clearWorkspace,
  defaultWorkspace,
  type Composer,
  type Workspace,
} from "./workspace.js";
import { attachHost } from "./bridge.js";
import type { NoteReviewRequest, ReviewConfirmRequest, ViewPort, ViewRequest } from "./tools.js";

/** The page's fixed skeleton (index.html). A missing element is a broken page, not a runtime state. */
function required<T extends Element>(parent: ParentNode, selector: string): T {
  const element = parent.querySelector<T>(selector);
  if (!element) throw new Error(`Careline page is missing ${selector}`);
  return element;
}
/** A `data-*` value the app's own markup always renders on this element. */
function attr(element: HTMLElement, name: string): string {
  const value = element.dataset[name];
  if (value === undefined) throw new Error(`Careline markup is missing data-${name}`);
  return value;
}
const root = required<HTMLElement>(document, "#careline");
const app = required<HTMLElement>(root, "#cl-app");
const message = required<HTMLElement>(root, "#cl-message");
const ENTITIES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};
const escape = (value: unknown) => String(value ?? "").replace(/[&<>"']/g, (c) => ENTITIES[c] ?? c);
const button = (text: string, action: string, primary = false, extra = "") =>
  `<button type="button" class="cl-button ${primary ? "primary" : ""}" data-action="${action}" ${extra}>${text}</button>`;
const link = (text: string, action: string, extra = "") =>
  `<button type="button" class="cl-link" data-action="${action}" ${extra}>${text}</button>`;
const badge = (text: string, tone = "") => `<span class="cl-badge ${tone}">${escape(text)}</span>`;
const panel = (title: string, content: string, action = "") =>
  `<section class="cl-panel"><div class="cl-panel-head"><h3>${title}</h3>${action}</div>${content}</section>`;
const body = (content: string) => `<div class="cl-panel-body">${content}</div>`;
const field = (label: string, name: string, value = "", type = "text", required = true) =>
  `<label class="cl-field">${label}<input name="${name}" type="${type}" value="${escape(value)}" ${required ? "required" : ""}></label>`;
const select = (label: string, name: string, items: readonly string[]) =>
  `<label class="cl-field">${label}<select name="${name}">${items.map((x) => `<option>${escape(x)}</option>`).join("")}</select></label>`;
const textArea = (label: string, name: string, value = "", required = true) =>
  `<label class="cl-field">${label}<textarea name="${name}" ${required ? "required" : ""}>${escape(value)}</textarea></label>`;

let storage: Storage | undefined;
try {
  storage = window.localStorage;
} catch {
  /* Store reports session-only mode. */
}
const workspace = loadWorkspace(storage);
const composer = () => workspace.composers[patientId];
function setComposer(value: Composer) {
  workspace.composers[patientId] = value;
  saveWorkspace(storage, workspace);
}
function clearComposer() {
  delete workspace.composers[patientId];
  saveWorkspace(storage, workspace);
}
const store = createStore({
  storage,
  log: (event, detail) => console.info(event, detail),
  uiBlockers: (id) => (workspace.composers[id] ? ["Complete or cancel the unfinished order form."] : []),
});
let visits: Record<string, Encounter> = {};
const refresh = () => {
  if (!store.status().blocked)
    visits = Object.fromEntries(patients.map((p) => [p.id, store.encounter(p.id)]));
};
refresh();
let patientId: string = workspace.patient_id,
  page: Workspace["page"] = workspace.page,
  globalPage: Workspace["global_page"] = workspace.global_page,
  filter: Workspace["filter"] = workspace.filter,
  selectedResult = workspace.selected_result,
  selectedVisit = workspace.selected_visit,
  orderType: Workspace["order_type"] = workspace.order_type,
  editOrderId: string | null = workspace.edit_order_id;
function remember() {
  Object.assign(workspace, {
    patient_id: patientId,
    page,
    global_page: globalPage,
    filter,
    selected_result: selectedResult,
    selected_visit: selectedVisit,
    order_type: orderType,
    edit_order_id: editOrderId,
  });
  saveWorkspace(storage, workspace);
}
const editingOrder = () => visit().orders.find((o) => o.id === editOrderId);
let bridgeState = "Standalone browser",
  reviewedRevision: number | null = null;
const patient = () => getPatient(patientId);
const visit = (): Encounter => visits[patientId];
const navigation: [Workspace["page"], string][] = [
  ["overview", "Chart overview"],
  ["history", "Visit history"],
  ["results", "Labs & tests"],
  ["medications", "Medications"],
  ["background", "History & allergies"],
  ["encounter", "Visit note"],
  ["orders", "Orders"],
  ["summary", "Review & sign"],
];
function notify(text: string) {
  message.textContent = text;
}
function runtimeStatus() {
  const status = store.status();
  required<HTMLElement>(root, "#cl-runtime-status").textContent =
    bridgeState +
    " · " +
    (status.persistence === "local" ? "Saved in this browser" : "Session-only storage") +
    (status.issue ? " · " + status.issue : "");
}
function currentMedications() {
  return medicationsFor(patientId, visit()).map((m): [string, string, string, string, Order | undefined] => [
    m.name,
    m.strength,
    m.directions,
    m.date,
    m.order,
  ]);
}
function run(action: string, payload: Record<string, unknown> = {}, revision = visit().revision): Result {
  const result = store.run(
    action,
    { patient_id: patientId, encounter_id: visit().id, expected_revision: revision, request_id: randomUuid() },
    payload,
  );
  refresh();
  runtimeStatus();
  return result;
}
function chartOrders({ awaitingResults = false } = {}) {
  const rows = visit()
    .orders.map((order) => ({ order }))
    .filter(
      ({ order }) =>
        !awaitingResults ||
        (["Laboratory", "Imaging", "Diagnostic test"].includes(order.type) &&
          order.status === "Ordered (simulated)" &&
          (filter === "All" || filter === order.type)),
    );
  if (!rows.length) return "";
  return panel(
    awaitingResults ? "Orders awaiting results" : "Orders this visit",
    `<ul class="cl-list">${rows.map(({ order }) => `<li><div class="cl-row" style="justify-content:space-between"><div>${link(escape(order.name) + (order.strength ? " " + escape(order.strength) : ""), "view-order", `data-order="${escape(order.id)}"`)}<p>${escape(order.type)} · Sep 28, 2026</p></div>${badge(awaitingResults ? "Awaiting result" : order.status, order.status === "Draft" ? "neutral" : "")}</div>${awaitingResults ? "<p>No result received yet.</p>" : ""}</li>`).join("")}</ul>`,
    link("All orders", "navigate", 'data-page="orders"'),
  );
}
function completedVisit() {
  if (!visit().signed) return "";
  return panel(
    "Sep 28, 2026",
    body(
      `<h3>${escape(patient().reason)}</h3><p class="cl-summary-lines">${escape(visit().note.assessment)}</p><div class="cl-row" style="justify-content:space-between;margin-top:14px"><small>Dr. Sarah Chen · Office visit</small>${link("Open encounter →", "navigate", 'data-page="summary"')}</div>`,
    ),
    badge("Signed"),
  );
}
const results = () => resultsFor(patientId);
function heading(title: string, sub = "", actions = "") {
  return `<div class="cl-heading"><div><h2>${title}</h2>${sub ? `<p>${sub}</p>` : ""}</div>${actions ? `<div class="cl-row">${actions}</div>` : ""}</div>`;
}
function checklist() {
  const v = visit();
  const steps: [string, boolean, string][] = [
    ["Medication reconciliation", v.medReviewed, "medications"],
    ["Allergies & history", v.allergyReviewed && v.historyReviewed, "background"],
    ["Encounter note", v.saved, "encounter"],
    ["Review & sign", v.signed, "summary"],
  ];
  return steps
    .map(
      ([name, done, dest], i) =>
        `<div class="cl-check"><span class="cl-step ${done ? "done" : ""}">${done ? "✓" : i + 1}</span>${link(name, "navigate", `data-page="${dest}"`)}${done ? '<span class="cl-meta" style="margin-left:auto">Done</span>' : ""}</div>`,
    )
    .join("");
}
function overview() {
  const p = patient(),
    v = visit();
  return (
    heading("Chart overview", `Current visit · ${p.reason}`, button("Open visit note", "note", true)) +
    `<div class="cl-warning"><strong>${escape(p.allergy)}</strong><span>${escape(p.reaction)}</span>${link(v.allergyReviewed ? "Allergies reviewed" : "Review allergies", "navigate", 'data-page="background"')}</div>` +
    `<div class="cl-grid"><div class="cl-stack">${panel("Last visit", body(`<div class="cl-row" style="justify-content:space-between;margin-bottom:12px"><strong>${v.signed ? "Sep 28, 2026" : escape(p.lastDate)}</strong>${badge("Office visit", "neutral")}</div><p class="cl-summary-lines">${escape(v.signed ? v.note.assessment : p.last)}</p><p class="cl-meta">Dr. Sarah Chen · Signed encounter</p>`), v.signed ? link("View note", "navigate", 'data-page="summary"') : link("View note", "past-visit", 'data-index="0"'))}
${panel(
  "Vitals",
  `<div class="cl-vitals">${[
    ["Blood pressure", p.bp, "mmHg"],
    ["Heart rate", p.pulse, "bpm"],
    ["Weight", p.weight, "lb"],
    ["BMI", p.bmi, "kg/m²"],
  ]
    .map(
      ([label, value, unit]) =>
        `<div><span>${label}</span><strong>${value}</strong><span>${unit}</span></div>`,
    )
    .join(
      "",
    )}</div><div style="padding:0 16px 13px" class="cl-meta">${p.status === "Scheduled" ? "From prior visit" : "Recorded today · 9:22 AM · A. Patel, MA"}</div>`,
)}
${panel(
  "Recent results",
  `<ul class="cl-list">${results()
    .slice(0, 3)
    .map(
      (r, i) =>
        `<li><div class="cl-row" style="justify-content:space-between"><div>${link(r.name, "result", `data-index="${i}"`)}<p>${r.date}</p></div><div style="text-align:right"><strong>${r.value} <small>${r.unit}</small></strong><p>${badge(r.flag, r.flag === "High" ? "warn" : "")}</p></div></div></li>`,
    )
    .join("")}</ul>`,
  link("All results", "navigate", 'data-page="results"'),
)}</div>
<div class="cl-stack">${panel("This visit", body(`<p class="cl-meta">Sep 28, 2026 · ${p.time}</p><h3 style="margin:6px 0 8px">${escape(p.reason)}</h3>${checklist()}`), badge(v.signed ? "Signed" : "In progress", v.signed ? "" : "neutral"))}
${panel("Active problems", `<ul class="cl-list">${p.problems.map((x) => `<li><strong style="font-size:12px">${escape(x)}</strong><p>Active · longitudinal chart</p></li>`).join("")}</ul>`)}
${chartOrders()}${panel(
      "Current medications",
      `<ul class="cl-list">${currentMedications()
        .map(
          (m) =>
            `<li><strong style="font-size:12px">${escape(m[0])} ${escape(m[1])}</strong><p>${escape(m[2])}</p></li>`,
        )
        .join("")}</ul>`,
      link("Review", "navigate", 'data-page="medications"'),
    )}</div></div>`
  );
}
function history() {
  const p = patient();
  const items = [
    { date: p.lastDate, title: p.reason, desc: p.last },
    {
      date: "Mar 12, 2026",
      title: "Routine follow-up",
      desc: "Chronic conditions and medications reviewed. Office ECG completed. Follow-up plan documented.",
    },
    {
      date: "Nov 4, 2025",
      title: "Cough",
      desc: "Office visit for persistent cough. Chest imaging completed and result reviewed.",
    },
  ];
  return (
    heading("Visit history", "Signed encounters and prior care plans") +
    `<div class="cl-timeline">${visit().signed ? `<div class="cl-timeline-item">${completedVisit()}</div>` : ""}${items.map((x, i) => `<div class="cl-timeline-item">${panel(x.date, body(`<h3>${escape(x.title)}</h3><p class="cl-muted" style="margin-top:7px">${escape(x.desc)}</p><div class="cl-row" style="justify-content:space-between;margin-top:14px"><small>Dr. Sarah Chen · Office visit</small>${link("Open encounter →", "past-visit", `data-index="${i}"`)}</div>`), badge("Signed"))}</div>`).join("")}</div>`
  );
}
function pastVisit() {
  const p = patient(),
    i = selectedVisit;
  const dates = [p.lastDate, "Mar 12, 2026", "Nov 4, 2025"];
  return (
    heading(
      "Previous encounter",
      `${dates[i]} · Dr. Sarah Chen`,
      button("Back to history", "navigate", false, 'data-page="history"'),
    ) +
    panel(
      i === 0 ? p.reason : i === 1 ? "Routine follow-up" : "Cough",
      body(
        `<div class="cl-label">Visit summary</div><p>${escape(i === 0 ? p.last : i === 1 ? "Reviewed chronic conditions, medications, and interval history. Office ECG report available in Labs & tests." : "Evaluated persistent cough. Chest X-ray ordered; final report documented no acute findings.")}</p><div class="cl-label cl-section-gap">Assessment & plan</div><p>${i === 0 ? "Medication list reconciled. Interval laboratory results reviewed. Repeat testing and follow-up documented." : "Discussed findings with patient. Follow-up instructions provided in the signed visit summary."}</p><div class="cl-label cl-section-gap">Linked records</div>${link("View labs & tests →", "navigate", 'data-page="results"')}<p class="cl-meta cl-section-gap">Electronically signed by Sarah Chen, MD · ${dates[i]}</p>`,
      ),
      badge("Signed · read only"),
    )
  );
}
function resultList() {
  const v = visit();
  return (
    heading(
      "Labs & tests",
      "Longitudinal laboratory, imaging, and diagnostic records",
      button("New order", "new-order", true, v.signed ? "disabled" : ""),
    ) +
    `<div class="cl-filter" aria-label="Result type">${["All", "Laboratory", "Imaging", "Diagnostic test"].map((x) => `<button data-action="filter" data-filter="${x}" aria-pressed="${filter === x}">${x === "Diagnostic test" ? "Diagnostic tests" : x}</button>`).join("")}</div>${chartOrders({ awaitingResults: true })}<section class="cl-panel cl-section-gap"><table class="cl-table"><thead><tr><th>Test / collected</th><th>Result</th><th>Status</th><th></th></tr></thead><tbody>${results()
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => filter === "All" || r.kind === filter)
      .map(
        ({ r, i }) =>
          `<tr><td><strong>${r.name}</strong><p class="cl-meta">${r.date} · ${r.kind}</p></td><td data-label="Result">${r.value} <small>${r.unit}</small></td><td data-label="Status">${badge(r.flag, r.flag === "High" ? "warn" : "neutral")}<p class="cl-meta">${v.resultReviewed.includes(i) ? "Reviewed" : "Not reviewed"}</p></td><td>${link("View result", "result", `data-index="${i}"`)}</td></tr>`,
      )
      .join("")}</tbody></table></section>`
  );
}
function resultDetail() {
  const r = results()[selectedResult],
    v = visit();
  return (
    heading(
      r.name,
      `${r.kind} · Final report`,
      button("Back to results", "navigate", false, 'data-page="results"'),
    ) +
    `<div class="cl-two">${panel("Result", body(`${badge(r.flag, r.flag === "High" ? "warn" : "")}<div class="cl-result-number">${r.value} <small>${r.unit}</small></div><p class="cl-meta">Reference: ${r.range}</p><p>${r.detail}</p>${r.previous ? `<div class="cl-label cl-section-gap">Result history</div><div class="cl-mini-trend"><div><strong>${r.previous}</strong><span></span>Jun 2026</div><div><strong>${r.value}</strong><span></span>Sep 2026</div></div>` : ""}`))}${panel("Report details", body(`<p><small>Collected</small><br>${r.collected}</p><p><small>Specimen / source</small><br>${r.specimen}</p><p><small>Performing facility</small><br>Oak & Elm Diagnostic Services (demo)</p><p><small>Ordering clinician</small><br>Sarah Chen, MD</p>`))}</div><div class="cl-actions">${button(v.resultReviewed.includes(selectedResult) ? "Reviewed ✓" : "Mark as reviewed", "review-result", true, v.signed || v.resultReviewed.includes(selectedResult) ? "disabled" : "")}</div>`
  );
}
function medications() {
  const p = patient(),
    v = visit();
  return (
    heading(
      "Medications",
      "Active medications and prescription history",
      button("New prescription", "new-rx", true, v.signed ? "disabled" : ""),
    ) +
    `<div class="cl-warning"><strong>Allergies</strong> ${escape(p.allergy)} · ${escape(p.reaction)}</div>${panel(
      "Active medications",
      `<table class="cl-table"><thead><tr><th>Medication</th><th>Directions</th><th>Last prescribed</th><th></th></tr></thead><tbody>${currentMedications()
        .map(
          (m, i) =>
            `<tr><td><strong>${escape(m[0])} ${escape(m[1])}</strong><p class="cl-meta">Active</p></td><td>${escape(m[2])}</td><td>${escape(m[3])}</td><td>${link("Renew", "renew", `data-index="${i}" ${v.signed ? "disabled" : ""}`)}</td></tr>`,
        )
        .join("")}</tbody></table>`,
    )}<div class="cl-row cl-section-gap" style="justify-content:space-between"><label class="cl-check"><input type="checkbox" data-review="medReviewed" ${v.medReviewed ? "checked" : ""} ${v.signed ? "disabled" : ""}>Medication list reviewed with patient</label>${badge(v.medReviewed ? "Reconciled" : "Review needed", v.medReviewed ? "" : "neutral")}</div><div class="cl-section-gap">${panel(
      "Prescription history",
      `<ul class="cl-list">${v.orders
        .filter((o) => o.type === "Prescription")
        .map(
          (o) =>
            `<li><strong>${escape(o.name)}${o.strength ? " " + escape(o.strength) : ""}</strong><p>Sep 28, 2026 · ${escape(o.pharmacy)} · ${escape(o.status)}</p></li>`,
        )
        .join(
          "",
        )}${p.meds.map((m) => `<li><div class="cl-row" style="justify-content:space-between"><strong style="font-size:12px">${escape(m[0])} ${escape(m[1])}</strong>${badge("Historical", "neutral")}</div><p>${escape(m[3])} · Oak Street Pharmacy · Dr. Sarah Chen</p></li>`).join("")}</ul>`,
    )}</div>`
  );
}
function background() {
  const p = patient(),
    v = visit();
  return (
    heading("History & allergies", "Clinical context for this encounter") +
    `<div class="cl-grid"><div class="cl-stack">${panel("Allergies", body(`<div class="cl-row" style="justify-content:space-between"><strong>${escape(p.allergy)}</strong>${badge(p.allergy.startsWith("No known") ? "No known allergies" : "Drug allergy", p.allergy.startsWith("No known") ? "" : "warn")}</div><p class="cl-muted" style="margin-top:8px">${escape(p.reaction)}</p><label class="cl-check cl-section-gap"><input type="checkbox" data-review="allergyReviewed" ${v.allergyReviewed ? "checked" : ""} ${v.signed ? "disabled" : ""}>Allergies reviewed with patient</label>`))}${panel("Medical & surgical history", body(`<div class="cl-label">Medical history</div><p>${p.problems.map(escape).join("<br>")}</p><div class="cl-label cl-section-gap">Surgical history</div><p>${escape(p.surgery)}</p>`))}${panel("Family & social history", body(`<div class="cl-label">Family</div><p>${escape(p.family)}</p><div class="cl-label cl-section-gap">Social</div><p>${escape(p.social)}</p><label class="cl-check cl-section-gap"><input type="checkbox" data-review="historyReviewed" ${v.historyReviewed ? "checked" : ""} ${v.signed ? "disabled" : ""}>History reviewed with patient</label>`))}</div><div class="cl-stack">${panel("Preventive care", `<ul class="cl-list"><li><strong style="font-size:12px">Influenza vaccination</strong><p>Discuss at current visit · Status not recorded this season</p></li><li><strong style="font-size:12px">Colorectal screening</strong><p>Prior record: 2023 · Review interval with patient</p></li>${p.id === "10428" ? '<li><strong style="font-size:12px">Diabetic eye examination</strong><p>Outside report received Jan 2026</p></li>' : ""}</ul>`)}${panel("Preferred pharmacy", body("<h3>Oak Street Pharmacy</h3><p>124 Oak Street, Springfield<br>Demo pharmacy · No transmission connection</p>"))}</div></div>`
  );
}
// ⭐ A note draft from Dobby waits HERE for the clinician (bridge `update_note`, `presents_ui`). Owner, 2026-10-07:
// a real planner wrote HPI/exam text nobody gave it and signed, behind an approval that showed only "Update Note".
// Nothing reaches the chart until the clinician saves this card; what they save — edits included — is what is written.
let noteReview: {
  request: NoteReviewRequest;
  resolve: (note: Partial<Note>) => void;
  reject: (error: Error) => void;
} | null = null;
const noteLabels = {
  hpi: "History of present illness",
  exam: "Exam & relevant findings",
  assessment: "Assessment & plan",
  instructions: "After-visit instructions",
} as const;
const followups = ["3 months", "2 weeks", "1 month", "6 months", "As needed"] as const;
function noteReviewCard(r: NoteReviewRequest, current: Note) {
  const sections = (Object.keys(noteLabels) as (keyof typeof noteLabels)[])
    .filter((k) => r.note[k] !== undefined)
    .map(
      (k) =>
        textArea(noteLabels[k], k, r.note[k] ?? "", false) +
        `<p class="cl-meta">${current[k].trim() ? `Currently: ${escape(current[k])}` : "Currently empty."}</p>`,
    )
    .join("");
  const followup = r.note.followup
    ? select("Follow-up interval", "followup", followups) + `<p class="cl-meta">Currently: ${escape(current.followup)}</p>`
    : "";
  return (
    heading("Visit note", "Dobby drafted note sections for your review", badge("Waiting on you", "neutral")) +
    `<form id="cl-note-review-form" class="cl-form">${panel(
      "Review Dobby's draft",
      body(
        `<div class="cl-warning"><strong>Nothing is written to the chart until you save.</strong> Check every statement against what the patient said and what you found; edit or clear anything you cannot confirm. A cleared section is not written.</div><div class="cl-form">${sections}${followup}</div>`,
      ),
    )}<div class="cl-actions">${button("Discard draft", "discard-note-review")}<button class="cl-button primary" type="submit">Save to note</button></div></form>`
  );
}
/** Settle the waiting draft and close its card. */
function settleNoteReview(outcome: { note: Partial<Note> } | { error: AppError }) {
  const r = noteReview;
  noteReview = null;
  if (!r) return;
  if ("note" in outcome) r.resolve(outcome.note);
  else r.reject(outcome.error);
}
// ⭐ A review attestation from Dobby waits HERE for the clinician (bridge `set_review_status` / `review_result`,
// `presents_ui`). Owner, 2026-10-07: a real planner marked meds, allergies and history reviewed merely because it had
// read them. A review is the clinician's statement, so nothing is recorded until THEY confirm it on this card.
let reviewAsk: { request: ReviewConfirmRequest; resolve: () => void; reject: (error: Error) => void } | null = null;
const reviewLabels = {
  medReviewed: "Medication list reviewed with patient",
  allergyReviewed: "Allergies reviewed with patient",
  historyReviewed: "History reviewed with patient",
} as const;
/** The chart page whose checkbox (or results) the attestation is about: the card shows there and nowhere else. */
function reviewAskPage(r: ReviewConfirmRequest): Workspace["page"] {
  if (r.item.kind === "results") return r.item.result_ids.length === 1 ? "result-detail" : "results";
  return r.item.field === "medReviewed" ? "medications" : "background";
}
function reviewAskHere() {
  const r = reviewAsk?.request;
  if (!r || globalPage !== "chart" || r.patient_id !== patientId || page !== reviewAskPage(r)) return false;
  return r.item.kind !== "results" || page === "results" || results()[selectedResult]?.id === r.item.result_ids[0];
}
function reviewAskCard() {
  if (!reviewAsk || !reviewAskHere()) return "";
  const item = reviewAsk.request.item;
  const what =
    item.kind === "results"
      ? `Mark ${listed(item.result_ids.map((id) => `<strong>${escape(results().find((x) => x.id === id)?.name ?? "a result")}</strong>`))} as reviewed by you`
      : item.reviewed
        ? `Record <strong>${escape(reviewLabels[item.field])}</strong>`
        : `Undo <strong>${escape(reviewLabels[item.field])}</strong>`;
  return `<section id="cl-review-confirm" class="cl-panel" aria-live="polite">${body(
    `<div class="cl-row" style="justify-content:space-between"><h3>Dobby asks you to confirm</h3>${badge("Waiting on you", "neutral")}</div><p>${what}?</p><p class="cl-meta">Nothing is recorded until you confirm. Confirm only what you reviewed yourself.</p><div class="cl-actions">${button("Not yet", "decline-review")}${button("Confirm review", "confirm-review", true)}</div>`,
  )}</section>`;
}
/** "A", "A and B", "A, B and C". */
function listed(items: string[]) {
  return items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}` : items[0];
}
/** Settle the waiting attestation and close its card. */
function settleReviewAsk(outcome: { confirmed: true } | { error: AppError }) {
  const r = reviewAsk;
  reviewAsk = null;
  if (!r) return;
  if ("confirmed" in outcome) r.resolve();
  else r.reject(outcome.error);
}
function encounter() {
  const p = patient(),
    v = visit(),
    n = v.note;
  if (v.signed) return heading("Visit note", "Signed Sep 28, 2026 · Sarah Chen, MD") + signedNote();
  if (noteReview?.request.patient_id === p.id) return noteReviewCard(noteReview.request, n);
  return (
    heading(
      "Visit note",
      `${p.reason} · Office visit`,
      `<span id="cl-note-status">${badge(v.saved ? "Draft saved" : "Unsaved draft", "neutral")}</span>`,
    ) +
    `<form id="cl-note-form" class="cl-form">${panel("Encounter documentation", body(`<div class="cl-form">${textArea("History of present illness", "hpi", n.hpi)}${textArea("Exam & relevant findings", "exam", n.exam)}${textArea("Assessment & plan", "assessment", n.assessment)}</div>`), button("Use demo note", "demo-note"))}${panel("Visit diagnoses", body(`<div class="cl-form">${p.problems.map((x) => `<label class="cl-check"><input type="checkbox" name="diagnoses" value="${escape(x)}" ${v.diagnoses.includes(x) ? "checked" : ""}>${escape(x)}</label>`).join("")}<p class="cl-meta">Select the conditions addressed at this encounter.</p></div>`))}${panel("Patient instructions & follow-up", body(`<div class="cl-form">${textArea("After-visit instructions", "instructions", n.instructions)}${select("Follow-up interval", "followup", ["3 months", "2 weeks", "1 month", "6 months", "As needed"])}</div>`))}<div class="cl-actions">${button("Review & sign", "navigate", false, 'data-page="summary"')}<button class="cl-button primary" type="submit">Save draft</button></div></form>`
  );
}
function orderList() {
  const v = visit();
  return (
    heading(
      "Orders",
      "Orders associated with this visit",
      button("New order", "new-order", true, v.signed ? "disabled" : "") +
        button("Prescribe", "new-rx", false, v.signed ? "disabled" : ""),
    ) +
    panel(
      "Current encounter",
      v.orders.length
        ? `<ul class="cl-list">${v.orders.map((o) => `<li><div class="cl-row" style="justify-content:space-between"><div><strong>${escape(o.name)}${o.strength ? " " + escape(o.strength) : ""}</strong><p>${escape(o.type)} · ${escape(o.diagnosis)}</p><p>${escape(o.type === "Prescription" ? o.directions + " · " + o.pharmacy : o.priority + " · " + o.timing)}</p></div>${badge(o.status, o.status === "Draft" ? "neutral" : "")}</div>${o.status === "Draft" ? `<div class="cl-row" style="margin-top:10px">${link("Review & sign", "edit-order", `data-order="${escape(o.id)}"`)}${link("Remove", "remove-order", `data-order="${escape(o.id)}"`)}</div>` : `<p>Signed by Sarah Chen, MD · Sep 28, 2026 · ${o.type === "Prescription" ? "Simulated pharmacy transmission" : "Simulated order submission"}</p>`}</li>`).join("")}</ul>`
        : `<div class="cl-empty">No orders for this visit yet.<p class="cl-note">Add a lab, test, referral, or prescription.</p></div>`,
    ) +
    `${composer() ? '<p class="cl-note">An unfinished ' + escape(composer().type.toLowerCase()) + " form is retained. Reopen New order or Prescribe to continue.</p>" : ""}<p class="cl-note">Orders are reviewed and signed separately from the encounter note.</p>`
  );
}
function orderComposer() {
  const p = patient(),
    v = visit(),
    old = composer()?.type === orderType ? composer().data : (editingOrder() ?? null);
  const rx = orderType === "Prescription";
  return (
    heading(
      rx ? "New prescription" : "New order",
      `${p.name} · DOB ${p.dob} · MRN ${p.id}`,
      button("Back to orders", "navigate", false, 'data-page="orders"'),
    ) +
    `<div class="cl-warning"><strong>Allergies:</strong> ${escape(p.allergy)} · ${escape(p.reaction)}</div>${
      rx
        ? ""
        : `<div class="cl-filter" aria-label="Order category">${Object.keys(catalogs)
            .map(
              (x) =>
                `<button data-action="order-type" data-type="${x}" aria-pressed="${orderType === x}">${x}</button>`,
            )
            .join("")}</div>`
    }<form id="cl-order-form" class="cl-form" data-rx="${rx}">${panel(rx ? "Prescription details" : orderType, body(`<div class="cl-form">${rx ? `<div class="cl-form-grid">${field("Medication name", "name", old?.name || "")}${field("Strength", "strength", old?.strength || "")}</div>${textArea("Directions (dose, route, frequency)", "directions", old?.directions || "")}<div class="cl-form-grid">${field("Quantity", "quantity", old?.quantity || "30", "number")}${field("Refills", "refills", old?.refills || "0", "number")}</div>${select("Pharmacy", "pharmacy", ["Oak Street Pharmacy · 124 Oak St", "Central Pharmacy · 220 Main St"])}` : `${select("Orderable", "name", catalogs[orderType as keyof typeof catalogs])}<div class="cl-form-grid">${select("Priority", "priority", ["Routine", "Urgent"])}${select("Timing", "timing", ["Within 1 week", "Today", "Before next visit"])}</div>`}${select("Associated diagnosis", "diagnosis", p.problems)}${textArea(rx ? "Pharmacy notes (optional)" : "Order instructions (optional)", "notes", old?.notes || "", false)}</div>`))}<div class="cl-row"><small>Demo only. No clinical decision support or external delivery.</small></div><div class="cl-actions">${button("Cancel", "cancel-order")}<button class="cl-button" type="submit" name="intent" value="draft">Save unsigned</button><button class="cl-button primary" type="submit" name="intent" value="review">Review ${rx ? "prescription" : "order"} →</button></div></form>`
  );
}
function reviewOrder() {
  const o = editingOrder();
  if (!o) return orderList();
  const draft = o.status === "Draft";
  return (
    heading(
      draft ? "Review before signing" : "Signed order",
      `${patient().name} · DOB ${patient().dob}`,
      draft ? button("Edit details", "edit-order", false, `data-order="${escape(o.id)}"`) : badge(o.status),
    ) +
    panel(
      escape(o.name) + (o.strength ? " " + escape(o.strength) : ""),
      body(
        `<div class="cl-label">${o.type}</div>${o.type === "Prescription" ? `<p><strong>Directions</strong><br>${escape(o.directions)}</p><p><strong>Quantity / refills</strong><br>${escape(o.quantity)} · ${escape(o.refills)} refills</p><p><strong>Destination</strong><br>${escape(o.pharmacy)}</p>` : `<p><strong>Priority / timing</strong><br>${escape(o.priority)} · ${escape(o.timing)}</p>`}<p><strong>Associated diagnosis</strong><br>${escape(o.diagnosis)}</p>${o.notes ? `<p><strong>Instructions</strong><br>${escape(o.notes)}</p>` : ""}<p class="cl-meta">${draft ? (o.type === "Prescription" ? "Signing will simulate sending this prescription to the selected pharmacy." : "Signing will place this order in the simulated encounter record.") : "Signed by Sarah Chen, MD · Sep 28, 2026. " + (o.type === "Prescription" ? "Pharmacy delivery simulated." : "Order submitted; awaiting completion.")}</p>`,
      ),
      badge(draft ? "Unsigned" : o.status, draft ? "neutral" : ""),
    ) +
    `<div class="cl-actions">${button("Back to orders", "navigate", false, 'data-page="orders"')}${draft ? button(o.type === "Prescription" ? "Sign & send to pharmacy" : "Sign & place order", "sign-order", true) : ""}</div>`
  );
}
const blockers = () => store.blockers(patientId);
function signedNote() {
  const n = visit().note;
  return panel(
    "Encounter documentation",
    body(
      [
        ["History of present illness", n.hpi],
        ["Exam & relevant findings", n.exam],
        ["Assessment & plan", n.assessment],
      ]
        .map(
          ([label, text]) =>
            `<div class="cl-label cl-section-gap">${label}</div><p class="cl-summary-lines">${escape(text)}</p>`,
        )
        .join(""),
    ),
    badge(visit().signed ? "Signed" : "Draft saved", visit().signed ? "" : "neutral"),
  );
}
function summary() {
  const v = visit(),
    issues = blockers();
  return (
    heading(v.signed ? "Visit complete" : "Review & sign", `${patient().reason} · Sep 28, 2026`) +
    (v.signed
      ? '<div class="cl-success"><strong>Encounter signed</strong><p style="margin-top:5px;font-size:12px">Sarah Chen, MD · Sep 28, 2026. After-visit summary is ready.</p></div>'
      : issues.length
        ? `<div class="cl-error"><strong>Before signing</strong><ul style="margin:7px 0 0;padding-left:20px">${issues.map((x) => `<li>${x}</li>`).join("")}</ul></div>`
        : '<div class="cl-success">All required sections are complete. Review the note and orders before signing.</div>') +
    `<div class="cl-grid cl-section-gap"><div class="cl-stack">${panel("Visit diagnoses", body(v.diagnoses.length ? v.diagnoses.map((x) => `<p>${escape(x)}</p>`).join("") : '<p class="cl-muted">No visit diagnoses selected.</p>'), v.signed ? "" : link("Edit note", "note"))}${v.saved ? signedNote() : panel("Encounter note", body('<p class="cl-muted">Document this visit before signing.</p>'), link("Open note", "note"))}${panel("After-visit summary", body(`<div class="cl-label">Patient instructions</div><p class="cl-summary-lines">${escape(v.note.instructions || "No instructions saved yet.")}</p><div class="cl-label cl-section-gap">Follow-up</div><p>${escape(v.note.followup)}</p><div class="cl-label cl-section-gap">Orders & prescriptions</div>${v.orders.length ? v.orders.map((o) => `<p>${escape(o.name)} ${o.strength ? escape(o.strength) : ""} · ${escape(o.status)}</p>`).join("") : "<p>No new orders.</p>"}`))}</div><div class="cl-stack">${panel("Completion checklist", body(checklist()))}${panel("Orders", body(`<p>${v.orders.length} total · ${v.orders.filter((o) => o.status === "Draft").length} unsigned</p>`), link("Review orders", "navigate", 'data-page="orders"'))}</div></div><div class="cl-actions">${v.signed ? button("Return to schedule", "schedule", true) : button("Sign & close encounter", "sign-visit", true, issues.length ? "disabled" : "")}</div>`
  );
}
function renderChart() {
  const p = patient(),
    v = visit();
  const screens = {
    overview,
    history,
    results: resultList,
    "result-detail": resultDetail,
    "past-visit": pastVisit,
    medications,
    background,
    encounter,
    orders: orderList,
    "order-compose": orderComposer,
    "order-review": reviewOrder,
    summary,
  };
  app.innerHTML = `<div class="cl-context">${link("← Patient schedule", "schedule")}<span class="cl-meta">Monday, September 28, 2026 · ${p.room}</span></div><section class="cl-patient" aria-label="Current patient"><div class="cl-patient-main"><div class="cl-avatar cl-patient-avatar">${p.initials}</div><div><div class="cl-row"><h1>${p.name}</h1>${badge(v.signed ? "Visit signed" : p.status, v.signed ? "" : "neutral")}</div><div class="cl-patient-meta"><span>${p.age} years · ${p.sex}</span><span>DOB ${p.dob}</span><span>MRN ${p.id}</span></div></div></div><div class="cl-row">${button("New order", "new-order", false, v.signed ? "disabled" : "")}${button(v.signed ? "View summary" : "Review & sign", "navigate", true, 'data-page="summary"')}</div></section><div class="cl-select-mobile"><label class="cl-field">Patient chart<select id="cl-mobile-nav" aria-label="Patient chart section">${navigation.map(([key, label]) => `<option value="${key}" ${page === key || (["order-compose", "order-review"].includes(page) && key === "orders") || (page === "result-detail" && key === "results") || (page === "past-visit" && key === "history") ? "selected" : ""}>${label}</option>`).join("")}</select></label></div><div class="cl-shell"><aside class="cl-side"><p class="cl-side-label">Patient chart</p><nav aria-label="Patient chart">${navigation.map(([key, label], i) => `${i === 5 ? '<hr><p class="cl-side-label">Current visit</p>' : ""}<button data-action="navigate" data-page="${key}" ${page === key ? 'aria-current="page"' : ""}>${label}</button>`).join("")}</nav><div class="cl-visit-state"><strong>${v.signed ? "✓ Encounter signed" : "● Encounter open"}</strong>Sarah Chen, MD<br>${p.time} · Office visit</div></aside><main class="cl-main">${reviewAskCard()}${(screens[page] || overview)()}</main></div>`;
  if (page === "encounter" && !v.signed) {
    const editor = app.querySelector<HTMLSelectElement>('#cl-note-form [name="followup"]');
    if (editor) editor.value = v.note.followup;
    const proposed = app.querySelector<HTMLSelectElement>('#cl-note-review-form [name="followup"]');
    if (proposed && noteReview?.request.note.followup) proposed.value = noteReview.request.note.followup;
  }
  if (page === "order-compose") {
    const form = required<HTMLFormElement>(app, "#cl-order-form");
    const c = composer();
    const old = c?.type === orderType ? c.data : (editingOrder() ?? null);
    if (old)
      Object.entries(old).forEach(([key, value]) => {
        const input = form.elements.namedItem(key) as HTMLInputElement | HTMLSelectElement | null;
        if (input && value !== undefined) input.value = String(value);
      });
    for (const name of ["quantity", "refills"]) {
      const input = form.elements.namedItem(name) as HTMLInputElement | null;
      if (input) {
        input.min = name === "quantity" ? "1" : "0";
        input.max = name === "quantity" ? "999" : "11";
        input.step = "1";
      }
    }
  }
}
function renderSchedule() {
  const isPatients = globalPage === "patients";
  app.innerHTML = `<main class="cl-schedule">${heading(isPatients ? "Patient directory" : "Today’s schedule", isPatients ? "3 fictional patient records" : "Monday, September 28, 2026 · Dr. Sarah Chen")}<div class="cl-row" style="justify-content:space-between;margin-bottom:20px"><label class="cl-field cl-search">Find a patient<input id="cl-patient-search" type="search" placeholder="Search name or MRN"></label><span class="cl-meta">3 patients · Primary care</span></div><section class="cl-panel" id="cl-patient-list">${patients.map((p) => `<div class="cl-schedule-row" data-patient-row="${p.id}"><div class="cl-schedule-time">${p.time.replace(" AM", "")}<div class="cl-meta">AM</div></div><div class="cl-schedule-person"><span class="cl-avatar">${p.initials}</span><div><strong>${p.name}</strong><p class="cl-meta">${p.age} years · ${p.reason}</p><p class="cl-meta">MRN ${p.id}</p></div></div>${badge(visits[p.id].signed ? "Complete" : p.status, visits[p.id].signed ? "" : "neutral")}${button("Open chart →", "open-patient", false, `data-patient="${p.id}"`)}</div>`).join("")}</section><div id="cl-no-patients" class="cl-empty" hidden>No patients match your search.</div></main>`;
}
function inbox() {
  app.innerHTML = `<main class="cl-schedule">${heading("Results inbox", "Final results awaiting clinician review")}<section class="cl-panel"><ul class="cl-list">${patients
    .map((p) => {
      const reviewed = visits[p.id].resultReviewed.includes(0);
      return `<li><div class="cl-row" style="justify-content:space-between"><div><strong>${p.name}</strong><p>Hemoglobin A1c · ${p.a1c}% · Sep 25, 2026</p></div><div class="cl-row">${badge(reviewed ? "Reviewed" : "Review needed", reviewed ? "" : "warn")}${button("Open result", "inbox-result", false, `data-patient="${p.id}"`)}</div></div></li>`;
    })
    .join("")}</ul></section></main>`;
}
function render() {
  root.querySelectorAll<HTMLElement>(".cl-topnav button").forEach((b) => {
    const active = globalPage === "chart" ? b.dataset.action === "patients" : b.dataset.action === globalPage;
    b.toggleAttribute("aria-current", active);
    if (active) b.setAttribute("aria-current", "page");
  });
  if (globalPage === "chart") renderChart();
  else if (globalPage === "inbox") inbox();
  else renderSchedule();
  runtimeStatus();
}

function captureNote() {
  const form = app.querySelector<HTMLFormElement>("#cl-note-form");
  if (!form || visit().signed) return;
  const data = new FormData(form),
    note = Object.fromEntries(
      ["hpi", "exam", "assessment", "instructions", "followup"].map((k) => [k, String(data.get(k) || "")]),
    );
  if (JSON.stringify(note) !== JSON.stringify(visit().note)) run("update_note", { note });
  const diagnoses = data.getAll("diagnoses").map(String);
  if (JSON.stringify(diagnoses) !== JSON.stringify(visit().diagnoses)) run("set_diagnoses", { diagnoses });
}
// Typing is not a record change: note text is committed on change/blur, navigation, save,
// page hide or after a short pause; unfinished order forms stay in the UI workspace.
let noteTimer: ReturnType<typeof setTimeout> | undefined;
function commitNote() {
  clearTimeout(noteTimer);
  noteTimer = undefined;
  captureNote();
}
function scheduleNoteCommit() {
  clearTimeout(noteTimer);
  noteTimer = setTimeout(() => safely(commitNote), 800);
}
function captureOrder() {
  const form = app.querySelector<HTMLFormElement>("#cl-order-form");
  if (!form || visit().signed) return;
  const next: Composer = {
    type: orderType,
    order_id: editOrderId,
    data: Object.fromEntries([...new FormData(form)].map(([k, v]) => [k, String(v)])),
  };
  if (JSON.stringify(next) !== JSON.stringify(composer())) setComposer(next);
}
function draw() {
  refresh();
  if (store.status().blocked) {
    app.innerHTML = heading(
      "Demo data unavailable",
      escape(store.status().issue),
      button("Reload page", "reload-page", true) + button("Reset demo", "reset"),
    );
    runtimeStatus();
    return;
  }
  if (editOrderId && !editingOrder()) editOrderId = null;
  if (page === "order-review" && !editingOrder()) page = "orders";
  if (noteReview && !(globalPage === "chart" && page === "encounter" && patientId === noteReview.request.patient_id)) {
    settleNoteReview({ error: new AppError("cancelled", "The clinician left the note draft; nothing was written.") });
    notify("Dobby's note draft was discarded; nothing was written.");
  }
  if (reviewAsk && !reviewAskHere()) {
    settleReviewAsk({ error: new AppError("cancelled", "The clinician left without confirming the review; nothing was recorded.") });
    notify("Nothing was recorded; Dobby's review request was dismissed.");
  }
  render();
  reviewedRevision = visit().revision;
  remember();
}
/** `dest` comes from the app's own `data-page` markup; an unknown page renders the overview. */
function goto(dest: string) {
  commitNote();
  captureOrder();
  page = dest as Workspace["page"];
  globalPage = "chart";
  notify("");
  draw();
}
function safely(fn: () => void) {
  try {
    fn();
  } catch (e) {
    notify(e instanceof AppError ? e.message : "Check the entered values and try again.");
    runtimeStatus();
  }
}
root.addEventListener("input", (e) =>
  safely(() => {
    const target = e.target as HTMLInputElement;
    if (target.closest("#cl-order-form")) captureOrder();
    if (target.closest("#cl-note-form")) {
      scheduleNoteCommit();
      const status = root.querySelector("#cl-note-status");
      if (status) status.innerHTML = badge("Unsaved draft", "neutral");
    }
    if (target.id === "cl-patient-search") {
      let count = 0;
      root.querySelectorAll<HTMLElement>("[data-patient-row]").forEach((row) => {
        const match = patientMatches(getPatient(row.dataset.patientRow ?? ""), target.value);
        row.hidden = !match;
        row.style.display = match ? "" : "none";
        if (match) count++;
      });
      required<HTMLElement>(root, "#cl-no-patients").hidden = count > 0;
    }
  }),
);
root.addEventListener("change", (e) =>
  safely(() => {
    const changed = e.target as HTMLInputElement;
    if (changed.closest("#cl-order-form")) captureOrder();
    if (changed.closest("#cl-note-form")) commitNote();
    if (changed.dataset.review) {
      run("set_review_status", { field: changed.dataset.review, reviewed: changed.checked });
      notify(changed.checked ? "Review recorded for this encounter." : "Review marked incomplete.");
    }
    if (changed.id === "cl-mobile-nav") goto(changed.value);
  }),
);
root.addEventListener("click", (e) =>
  safely(() => {
    const target = (e.target as Element).closest<HTMLButtonElement>("[data-action]");
    if (!target || target.disabled) return;
    const a = attr(target, "action");
    if (a === "reset") {
      app.innerHTML = `<main class="cl-schedule">${heading("Reset this demo?", "This clears this app’s fictional notes, reviews and orders only.")}<div class="cl-actions">${button("Keep current work", "keep-work")}${button("Reset all demo data", "confirm-reset", true)}</div></main>`;
      return;
    }
    if (a === "keep-work") {
      draw();
      return;
    }
    if (a === "reload-page") {
      location.reload();
      return;
    }
    if (a === "confirm-reset") {
      store.reset();
      clearWorkspace(storage);
      Object.assign(workspace, defaultWorkspace());
      ({
        patient_id: patientId,
        page,
        global_page: globalPage,
        filter,
        selected_result: selectedResult,
        selected_visit: selectedVisit,
        order_type: orderType,
        edit_order_id: editOrderId,
      } = workspace);
      draw();
      notify("Demo restored to its starting state.");
      return;
    }
    if (store.status().blocked) {
      draw();
      notify(store.status().issue);
      return;
    }
    if (a === "navigate") {
      goto(attr(target, "page"));
      return;
    }
    if (["schedule", "patients", "inbox"].includes(a)) {
      commitNote();
      captureOrder();
      globalPage = a as Workspace["global_page"];
      notify("");
      draw();
      return;
    }
    if (a === "open-patient" || a === "inbox-result") {
      commitNote();
      captureOrder();
      patientId = attr(target, "patient");
      selectedResult = 0;
      page = a === "inbox-result" ? "result-detail" : "overview";
      globalPage = "chart";
      draw();
      return;
    }
    if (a === "note") {
      goto("encounter");
      return;
    }
    if (a === "past-visit") {
      selectedVisit = Number(attr(target, "index"));
      goto("past-visit");
      return;
    }
    if (a === "result") {
      selectedResult = Number(attr(target, "index"));
      goto("result-detail");
      return;
    }
    if (a === "filter") {
      filter = attr(target, "filter") as Workspace["filter"];
      draw();
      return;
    }
    if (a === "review-result") {
      run("review_result", { result_ids: [results()[selectedResult].id] });
      draw();
      notify("Result reviewed by Sarah Chen, MD.");
      return;
    }
    if (a === "new-order" || a === "new-rx") {
      if (visit().signed) return;
      commitNote();
      captureOrder();
      const c = composer();
      orderType = a === "new-rx" ? "Prescription" : c && c.type !== "Prescription" ? c.type : "Laboratory";
      editOrderId = c?.type === orderType ? c.order_id : null;
      page = "order-compose";
      globalPage = "chart";
      draw();
      return;
    }
    if (a === "order-type") {
      orderType = attr(target, "type") as Workspace["order_type"];
      editOrderId = null;
      clearComposer();
      draw();
      return;
    }
    if (a === "cancel-order") {
      clearComposer();
      page = "orders";
      draw();
      return;
    }
    if (a === "renew") {
      const m = currentMedications()[Number(attr(target, "index"))];
      editOrderId = null;
      orderType = "Prescription";
      setComposer({
        type: orderType,
        order_id: null,
        data: {
          name: m[0],
          strength: m[1],
          directions: m[2],
          quantity: m[4]?.quantity || "30",
          refills: m[4]?.refills || "0",
          pharmacy: m[4]?.pharmacy || "Oak Street Pharmacy · 124 Oak St",
          diagnosis: m[4]?.diagnosis || patient().problems[0],
          notes: m[4]?.notes || "",
        },
      });
      page = "order-compose";
      draw();
      return;
    }
    if (a === "view-order") {
      editOrderId = attr(target, "order");
      goto("order-review");
      return;
    }
    if (a === "edit-order") {
      clearComposer();
      editOrderId = attr(target, "order");
      const o = editingOrder();
      if (!o) return;
      orderType = o.type;
      page = "order-compose";
      draw();
      return;
    }
    if (a === "remove-order") {
      const id = attr(target, "order");
      run("remove_order", { order_id: id });
      if (composer()?.order_id === id) clearComposer();
      draw();
      notify("Unsigned order removed.");
      return;
    }
    if (a === "sign-order") {
      const o = editingOrder();
      if (!o) return;
      run("sign_order", { order_id: o.id }, reviewedRevision ?? undefined);
      page = "orders";
      draw();
      notify(
        o.type === "Prescription"
          ? "Prescription signed. Pharmacy delivery simulated."
          : "Order signed and placed in the demo record.",
      );
      return;
    }
    if (a === "confirm-review" && reviewAsk) {
      // The bridge records it; the store event redraws the page.
      settleReviewAsk({ confirmed: true });
      return;
    }
    if (a === "decline-review") {
      settleReviewAsk({ error: new AppError("cancelled", "The clinician has not confirmed this review; nothing was recorded.") });
      draw();
      notify("Not recorded. Dobby was told you have not confirmed this review.");
      return;
    }
    if (a === "discard-note-review") {
      settleNoteReview({ error: new AppError("cancelled", "The clinician discarded the note draft; nothing was written.") });
      draw();
      notify("Dobby's note draft was discarded; nothing was written.");
      return;
    }
    if (a === "demo-note") {
      const p = patient();
      run("update_note", {
        note: {
          hpi: `${p.age}-year-old patient presents for ${p.reason.toLowerCase()}. Interval history and medication use reviewed. No new concerns reported in this fictional encounter.`,
          exam: `Alert and comfortable. BP ${p.bp} mmHg, pulse ${p.pulse} bpm. Findings documented for demonstration only.`,
          assessment:
            "Reviewed available results and discussed the current care plan. See linked encounter diagnoses and signed orders. Follow-up and return precautions discussed.",
          instructions:
            "Review the visit summary and current medication list. Contact the clinic with questions about the care plan. Complete any testing listed in the signed orders.",
          followup: "3 months",
        },
      });
      run("set_diagnoses", { diagnoses: [...p.problems] });
      draw();
      notify("Demo note inserted. Review and save the draft.");
      return;
    }
    if (a === "sign-visit") {
      run("sign_encounter", {}, reviewedRevision ?? undefined);
      draw();
      notify("Encounter signed. Summary ready; all actions are simulated.");
    }
  }),
);
root.addEventListener("submit", (e) => {
  e.preventDefault();
  safely(() => {
    const form = e.target as HTMLFormElement;
    if (form.id === "cl-note-review-form" && noteReview) {
      const data = new FormData(form);
      const note: Partial<Note> = {};
      for (const k of Object.keys(noteLabels) as (keyof typeof noteLabels)[]) {
        const value = String(data.get(k) ?? "").trim();
        if (noteReview.request.note[k] !== undefined && value) note[k] = value;
      }
      const followup = data.get("followup");
      if (noteReview.request.note.followup && followup) note.followup = String(followup) as Note["followup"];
      settleNoteReview({ note });
      // The bridge writes what was saved; the store event redraws the note.
      notify("Reviewed note sections saved to the draft. All actions are simulated.");
      return;
    }
    if (form.id === "cl-note-form") {
      commitNote();
      run("save_note");
      draw();
      notify("Draft saved for this encounter.");
      return;
    }
    if (form.id === "cl-order-form") {
      const data = Object.fromEntries([...new FormData(form)].map(([k, v]) => [k, String(v).trim()]));
      const order = { ...data, type: orderType };
      const result = editingOrder()
        ? run("update_order", { order_id: editOrderId, order })
        : run("create_order", { order });
      clearComposer();
      editOrderId = result.entity_id ?? null;
      page =
        ((e as SubmitEvent).submitter as HTMLButtonElement | null)?.value === "draft"
          ? "orders"
          : "order-review";
      globalPage = "chart";
      draw();
      if (page === "orders") notify("Unsigned order saved. Review and sign when ready.");
    }
  });
});
const view: ViewPort = {
  assertAvailable() {
    commitNote();
    if (
      app.querySelector("#cl-order-form") ||
      app.querySelector("#cl-note-form")?.contains(document.activeElement)
    )
      throw new AppError(
        "ui_busy",
        "A clinician is editing a form. Finish or leave the form before agent changes.",
      );
  },
  reviewNote(request, signal) {
    return new Promise<Partial<Note>>((resolve, reject) => {
      settleNoteReview({ error: new AppError("superseded", "A newer note draft replaced this one; nothing was written.") });
      // The host stopped waiting (the run ended or timed out): close the card so a late Save cannot write.
      const expire = () => {
        if (noteReview?.request !== request) return;
        settleNoteReview({ error: new AppError("expired", "Dobby stopped waiting for this note draft; nothing was written.") });
        draw();
        notify("Dobby stopped waiting; the note draft was closed and nothing was written.");
      };
      noteReview = {
        request,
        resolve: (note) => (signal.removeEventListener("abort", expire), resolve(note)),
        reject: (error) => (signal.removeEventListener("abort", expire), reject(error)),
      };
      if (signal.aborted) return expire();
      signal.addEventListener("abort", expire, { once: true });
      view.show({ patient_id: request.patient_id, section: "encounter" });
    });
  },
  confirmReview(request, signal) {
    return new Promise<void>((resolve, reject) => {
      settleReviewAsk({ error: new AppError("superseded", "A newer review request replaced this one; nothing was recorded.") });
      // The host stopped waiting (the run ended or timed out): close the card so a late Confirm cannot record.
      const expire = () => {
        if (reviewAsk?.request !== request) return;
        settleReviewAsk({ error: new AppError("expired", "Dobby stopped waiting for your confirmation; nothing was recorded.") });
        draw();
        notify("Dobby stopped waiting; the review request was closed and nothing was recorded.");
      };
      reviewAsk = {
        request,
        resolve: () => (signal.removeEventListener("abort", expire), resolve()),
        reject: (error) => (signal.removeEventListener("abort", expire), reject(error)),
      };
      if (signal.aborted) return expire();
      signal.addEventListener("abort", expire, { once: true });
      if (request.item.kind === "results" && request.item.result_ids.length === 1) {
        const id = request.item.result_ids[0];
        const index = resultsFor(request.patient_id).findIndex((r) => r.id === id);
        if (index >= 0) selectedResult = index;
      }
      view.show({ patient_id: request.patient_id, section: reviewAskPage(request) });
    });
  },
  show({ patient_id, section, order_id }: ViewRequest) {
    patientId = patient_id;
    page = section as Workspace["page"];
    globalPage = "chart";
    refresh();
    if (order_id) editOrderId = order_id;
    draw();
  },
};
store.subscribe((event) => {
  refresh();
  if (event.source === "agent") {
    const c = workspace.composers[event.patient_id];
    if (c?.order_id && !visits[event.patient_id].orders.some((o) => o.id === c.order_id)) {
      c.order_id = null;
      saveWorkspace(storage, workspace);
    }
    view.show({
      patient_id: event.patient_id,
      section:
        event.action.includes("order") || event.action === "renew_prescription"
          ? "orders"
          : event.action === "sign_encounter"
            ? "summary"
            : event.action === "update_note"
              ? "encounter"
              : event.action === "set_review_status" || event.action === "review_result"
                ? // The clinician just confirmed it on this page: stay where they are.
                  page
                : "overview",
    });
    notify("Dobby updated this fictional record. All actions are simulated.");
  }
});
window.addEventListener("pagehide", () => safely(commitNote));
window.addEventListener("storage", (event) => {
  if (event.key === STORAGE_KEY || event.key === null) {
    store.invalidate();
    runtimeStatus();
    notify(store.status().issue);
  }
});
draw();
attachHost(store, view, (state) => {
  bridgeState = state;
  console.info("ehr.bridge_state", { state });
  runtimeStatus();
});
